import {
  DEFAULT_ROUTINE_ICON,
  type Report,
  type Routine,
  useStores,
} from '@littlebird/sdk';
import {
  adaptRoutineToViewModel,
  devicesWord,
  isRunWaiting,
  isRoutineRunUnread,
} from '@littlebird/ui-utils';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icons } from '@/assets/icons/icons';
import { HiddenFromUserIcon } from '@/components/blocks/HiddenFromUserIcon';
import { SidebarMoreRow } from '@/components/blocks/Layout/Sidebar/SidebarMoreRow';
import { SidebarSectionHeading } from '@/components/blocks/Layout/Sidebar/SidebarSectionContainer';
import { SidebarSectionRow } from '@/components/blocks/Layout/Sidebar/SidebarSectionRow';
import { useStickySidebarSection } from '@/components/blocks/Layout/Sidebar/sticky-section-context';
import { SECTION_GAP } from '@/components/blocks/Layout/Sidebar/style';
import { XStack, YStack } from '@/components/ui';
import { EntityIcon } from '@/components/ui/icon-picker';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import { SidebarRow } from '@/design-system-v2/sidebar-row';
import { useReportBody } from '@/features/routines/report-body';
import { formatNoteDate } from '@/features/routines/routines.utils';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { cn } from '@/lib/utils';
import { useDrawerNavigation } from '@/providers/DrawerNavigationProvider';
import { settingsStore } from '@/stores/settings.store';
import { Panel, uiStore } from '@/stores/ui.store';
import { formatTimeFromHHmm } from '@/utils/date';

import { useActiveRoutines } from '../hooks/use-active-routines';
import { useOpenRoutine } from '../hooks/use-open-routine';

import { RoutineActionsMenuItems } from './routine-actions-menu';

// Planned: restore when routine project assignment is implemented.
// import { RoutineProjectDropdownSubMenu } from './routine-project-menu';

const ROUTINE_PREVIEW_LIMIT = 5;

const formatRunDate = (date: string | Date) => {
  const formattedDate = formatNoteDate(date)
    .replace(/^(Today|Yesterday)/, (relativeDay) => relativeDay.toLowerCase())
    .replace(/, ([^,]+)$/, ' at $1');

  return `Ran ${formattedDate}`;
};

type RoutineView = 'canvas' | 'chat' | 'configure';

type RoutineSidebarRowProps = {
  routine: Routine;
  report?: Report;
  isSelected: boolean;
  isUnread: boolean;
  view?: RoutineView;
  onSelect: (routine: Routine, event?: React.MouseEvent) => void;
  onOpenInTab: (routine: Routine) => void;
  onNavigate: () => void;
};

const SectionNote = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={cn(
      'px-[var(--row-pad,8px)] py-1.5 text-xs text-foreground-muted',
      className
    )}
  >
    {children}
  </p>
);

const RoutineSidebarRow = observer(
  ({
    routine,
    report,
    isSelected,
    isUnread,
    view,
    onSelect,
    onOpenInTab,
    onNavigate,
  }: RoutineSidebarRowProps) => {
    void settingsStore.timeFormat;
    const reportBody = useReportBody(report?.artifactFileIds);
    const routineViewModel = adaptRoutineToViewModel(routine, [], {
      formatTime: formatTimeFromHHmm,
    });
    const hasCanvas =
      reportBody.isCanvas && reportBody.artifact.status === 'ready';
    const isWaiting = report && isRunWaiting(report);
    const statusLabel = isWaiting
      ? `Waiting for ${devicesWord(report.waitingOn)}`
      : report && !reportBody.isCanvas
        ? formatRunDate(report.scheduledAt)
        : routineViewModel.schedule.summary;
    const menuProps = {
      routine,
      statusLabel,
      hasCanvas,
      view: isSelected ? view : undefined,
      reportId: report?.referenceId ?? null,
      source: 'sidebar' as const,
      onNavigate,
    };

    return (
      <SidebarSectionRow
        item={routine}
        title={routine.title}
        isSelected={isSelected}
        onSelect={onSelect}
        onOpenInTab={onOpenInTab}
        panel={Panel.Routines}
        leftSide={
          <SidebarRow.Icon>
            <EntityIcon
              icon={routine.icon ?? DEFAULT_ROUTINE_ICON}
              size="100%"
              className="text-foreground-muted"
            />
          </SidebarRow.Icon>
        }
        menuContentClassName="min-w-52"
        contextMenuExtra={
          <RoutineActionsMenuItems variant="context" {...menuProps} />
        }
        dropdownMenuExtra={
          <RoutineActionsMenuItems variant="dropdown" {...menuProps} />
        }
        leadingUnread={isUnread}
        rightSide={
          !routine.hiddenFromUser ? null : (
            <XStack className="shrink-0 items-center gap-1">
              <HiddenFromUserIcon hidden={routine.hiddenFromUser} />
            </XStack>
          )
        }
      />
    );
  }
);

export const FavoriteRoutineSidebarRow = observer(
  ({ routineId }: { routineId: number }) => {
    const { routines, reports } = useStores();
    const { openRoutine, openRoutineInNewTab } = useOpenRoutine();
    const { setIsOpen } = useDrawerNavigation();
    const params = useParams({ strict: false });
    const { view } = useSearch({ strict: false }) as { view?: RoutineView };

    const routine = routines.getByReferenceId(routineId);
    if (!routine) return null;

    const report = reports.latestByTemplateId.get(routineId);
    const isUnread =
      report !== undefined &&
      isRoutineRunUnread(report.text, report.lastViewed !== null);

    return (
      <RoutineSidebarRow
        routine={routine}
        report={report}
        isSelected={String(routineId) === params.routineId}
        isUnread={isUnread}
        view={view}
        onSelect={(_, event) => {
          setIsOpen(false);
          openRoutine(routineId, event);
        }}
        onOpenInTab={() => openRoutineInNewTab(routineId)}
        onNavigate={() => setIsOpen(false)}
      />
    );
  }
);

export const RoutineChatSidebarSection = observer(() => {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const { openRoutine, openRoutineInNewTab } = useOpenRoutine();
  const { setIsOpen } = useDrawerNavigation();
  const { routineId } = useParams({ strict: false });
  const { view } = useSearch({ strict: false }) as {
    view?: 'canvas' | 'chat' | 'configure';
  };
  const {
    routines,
    activeRoutines,
    latestReportByRoutineId,
    isRoutineUnread,
    isLoading,
    areReportsLoading,
  } = useActiveRoutines();
  const isExpanded = uiStore.sidebarExpandedSections.routines ?? true;
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [showAllInSection, setShowAllInSection] = useState(false);
  const {
    onSectionLayoutChange,
    portalTarget,
    section: stickySection,
  } = useStickySidebarSection();
  const isStickyHeaderActive = stickySection === 'routines';

  const unstarredRoutines = activeRoutines.filter(
    (routine) => !routine.starred
  );
  const filteredRoutines = showUnreadOnly
    ? unstarredRoutines.filter(
        (routine) =>
          isRoutineUnread(routine.referenceId) ||
          String(routine.referenceId) === routineId
      )
    : unstarredRoutines;
  const hasMoreRoutines = filteredRoutines.length > ROUTINE_PREVIEW_LIMIT;
  const visibleRoutines = showAllInSection
    ? filteredRoutines
    : filteredRoutines.slice(0, ROUTINE_PREVIEW_LIMIT);
  const isEmptyUnreadView =
    showUnreadOnly &&
    !isLoading &&
    !areReportsLoading &&
    !visibleRoutines.length;
  const isBodyExpanded = isExpanded && !isEmptyUnreadView;

  useLayoutEffect(() => {
    onSectionLayoutChange();
  }, [isBodyExpanded, onSectionLayoutChange]);

  const selectRoutine = (routine: Routine, event?: React.MouseEvent) => {
    setIsOpen(false);
    openRoutine(routine.referenceId, event);
  };

  const openRoutineInTab = (routine: Routine) => {
    openRoutineInNewTab(routine.referenceId);
  };

  const showAllRoutines = (event?: React.MouseEvent) => {
    setIsOpen(false);
    if (event && isNewTabClick(event)) {
      openTab({ path: '/routines' });
    } else {
      navigate({ to: '/routines' });
    }
  };

  const renderSectionHeading = (isSticky: boolean) => (
    <SidebarSectionHeading
      aria-hidden={!isSticky && isStickyHeaderActive ? true : undefined}
      inert={!isSticky && isStickyHeaderActive ? true : undefined}
      data-sidebar-section-heading={isSticky ? undefined : 'routines'}
      className={cn(!isSticky && isStickyHeaderActive && 'invisible')}
      isExpanded={isBodyExpanded}
      onNavigate={showAllRoutines}
      onExpandedChange={(next) => {
        if (next && isEmptyUnreadView) setShowUnreadOnly(false);
        uiStore.setSidebarSectionExpanded('routines', next);
      }}
      rightSlot={
        <>
          <RoundIconButton
            label="Open all routines"
            icon={Icons.arrowUpRight}
            size="xs"
            className="-my-0.5"
            onClick={showAllRoutines}
          />
          <RoundIconButton
            label={showUnreadOnly ? 'Show all routines' : 'Only show unread'}
            icon={showUnreadOnly ? Icons.eyeOff : Icons.eye}
            size="xs"
            active={showUnreadOnly}
            aria-pressed={showUnreadOnly}
            className="-my-0.5"
            onClick={() => setShowUnreadOnly((showUnread) => !showUnread)}
          />
        </>
      }
    >
      Routines
    </SidebarSectionHeading>
  );

  return (
    <YStack
      data-sidebar-section-boundary="routines"
      className={cn('group/routine-section shrink-0', SECTION_GAP)}
    >
      {renderSectionHeading(false)}
      <YStack
        data-sidebar-section-item="routines"
        className={cn('mt-0.5 gap-0.5', !isBodyExpanded && 'hidden')}
      >
        {isLoading || (showUnreadOnly && areReportsLoading) ? (
          <SectionNote>Loading routines…</SectionNote>
        ) : !routines.length ? (
          <SectionNote className="text-xs/relaxed">
            Nothing yet — chats that run themselves.{' '}
            <button
              type="button"
              className="font-medium text-foreground-subtle underline-offset-2 hover:text-foreground hover:underline"
              onClick={showAllRoutines}
            >
              Learn more
            </button>
          </SectionNote>
        ) : !activeRoutines.length ? (
          <SectionNote>No active routines</SectionNote>
        ) : !unstarredRoutines.length ? (
          <SectionNote>All routines are in Favorites</SectionNote>
        ) : (
          <>
            {visibleRoutines.map((routine) => {
              const isSelected = String(routine.referenceId) === routineId;
              const report = latestReportByRoutineId.get(routine.referenceId);

              return (
                <RoutineSidebarRow
                  key={routine.referenceId}
                  routine={routine}
                  report={report}
                  isSelected={isSelected}
                  isUnread={isRoutineUnread(routine.referenceId)}
                  view={view}
                  onSelect={selectRoutine}
                  onOpenInTab={openRoutineInTab}
                  onNavigate={() => setIsOpen(false)}
                />
              );
            })}
            {!showAllInSection && hasMoreRoutines && (
              <SidebarMoreRow onClick={() => setShowAllInSection(true)} />
            )}
            {showAllInSection && hasMoreRoutines && (
              <XStack className="mt-1.5 items-center gap-1.5 px-[var(--row-pad)] text-xs text-foreground-muted">
                <button
                  type="button"
                  className="hover:text-foreground"
                  onClick={() => setShowAllInSection(false)}
                >
                  Show less
                </button>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  className="hover:text-foreground"
                  onClick={showAllRoutines}
                >
                  All routines
                </button>
              </XStack>
            )}
          </>
        )}
      </YStack>
      {portalTarget && isStickyHeaderActive
        ? createPortal(renderSectionHeading(true), portalTarget)
        : null}
    </YStack>
  );
});
